import { TagBidContent } from './TagBidContent.js';
import { SurfaceRequirements } from './SurfaceRequirements.js';

/**
 * A winning bid ready for rendering.
 * Mirrors Rust TagBid.
 */
export class TagBid {
  /** @param {Record<string, unknown>} raw */
  constructor(raw) {
    if (typeof raw.instance_id !== 'string' || raw.instance_id.length === 0)
      throw new Error('TagBid.instance_id must be a non-empty string');
    if (raw.source_id !== null && typeof raw.source_id !== 'string')
      throw new Error('TagBid.source_id must be a string or null');

    this._instanceId     = raw.instance_id;
    this._sourceId       = raw.source_id;
    this._container      = raw.container;
    this._content        = new TagBidContent(raw.content);
    this._surface        = new SurfaceRequirements(raw.surface);
    this._viewabilityUrl = raw.viewability_url || null;
  }

  /**
   * Container descriptor — determines where and how the creative is injected.
   *
   * Possible shapes:
   *   { type: 'in_place' }
   *   { type: 'in_target', config: { selector: string } }
   *
   * @returns {{ type: string, config?: Record<string, unknown> }}
   */
  get container() { return this._container; }

  /** Unique winning-bid instance used to scope publisher lifecycle events. */
  get instanceId() { return this._instanceId; }

  /** Direct Chattable paid-media source; explicit null for banner/RTB winners. */
  get sourceId() { return this._sourceId; }

  /**
   * The creative content to render.
   * @returns {TagBidContent}
   */
  get content() { return this._content; }

  /** Explicit initial and interactive surface requirements from the Exchange. */
  get surface() { return this._surface; }

  /** Whether the creative must mount directly so it can use the surface client. */
  get requiresDirectSurface() { return this._surface.requiresDirectRender; }

  /**
   * Viewability postback URL. When present, the tag tracks in-view
   * duration and POSTs `{ first, delta_ms }` to this URL.
   * Only set for direct-served ads.
   * @returns {string | null}
   */
  get viewabilityUrl() { return this._viewabilityUrl; }
}
